"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, FileCheck, Layers, Radio, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function PatFamily() {
  const prefersReducedMotion = useReducedMotion();
  const highlights = [{
    id: "real-time",
    title: "Real-Time Inline Monitoring",
    description: "Direct optical probe measurement into reactors, dryers, and blenders without sampling delay."
  }, {
    id: "gmp-ready",
    title: "GMP & 21 CFR Part 11 Compliant",
    description: "Complete data integrity, audit trails, and automated batch reporting for regulated manufacturing."
  }, {
    id: "multi-point",
    title: "Multi-Channel Multiplexing",
    description: "Connect up to 6 fiber optic measurement heads from a single rugged industrial spectrometer unit."
  }];
  const products = [{
    id: "matrix-f-ii",
    name: "MATRIX-F II",
    tagline: "Industrial FT-NIR Process Spectrometer",
    description: "High-precision FT-NIR spectrometer with automated background reference and fiber optic multiplexer for harsh plant environments.",
    link: "/MatrixFIIPage",
    badge: "Flagship FT-NIR"
  }, {
    id: "bruker-beam",
    name: "Bruker BEAM",
    tagline: "Compact Single-Point NIR Sensor",
    description: "Miniaturized inline NIR sensor engineered for solid dosage processes, fluid bed dryers, and fast belt conveyor monitoring.",
    link: "/UserBeamPage",
    badge: "Compact Inline"
  }, {
    id: "process-guardian",
    name: "Process Guardian",
    tagline: "Process Raman Reaction Analyzer",
    description: "Real-time Raman analyzer for aqueous phase bioprocessing, crystallization kinetics, and continuous synthesis control.",
    link: "/ProcessGuardianPage",
    badge: "Raman Analysis"
  }];
  return <section id="heropatfamily" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Subtle background decorative grid */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-muted/60 via-background to-background pointer-events-none" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column: Heading & Positioning */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="px-3 py-1 text-xs font-semibold gap-1.5 border-primary/30 text-foreground bg-primary/5">
                <Radio className="size-3.5 text-primary animate-pulse" />
                Process Analytical Technology
              </Badge>
              <span className="text-xs text-muted-foreground">Bruker Spectroscopy Family</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground leading-[1.15]">
              Real-Time Process Analytics for Modern Regulated Manufacturing
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-default">
              Transition from retrospective laboratory testing to continuous, inline quality verification. The Bruker PAT family delivers high-speed FT-NIR and Raman spectroscopy directly into pharmaceutical, chemical, and bioprocess streams across Ireland and the UK.
            </p>

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="rounded-md font-medium">
                <Link to="/ProcessAnalyticalTechnologyPage">
                  Explore PAT Solutions
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                <Link to="/BookADemoPage">
                  Book a Feasibility Study
                </Link>
              </Button>
            </div>

            {/* Value checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-border mt-2">
              <div className="flex items-start gap-2.5">
                <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="text-xs">
                  <span className="font-semibold text-foreground block">Zero Sampling Loss</span>
                  <span className="text-muted-foreground">Non-destructive optics</span>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Cpu className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="text-xs">
                  <span className="font-semibold text-foreground block">OPC UA / SIPAT</span>
                  <span className="text-muted-foreground">Seamless SCADA integration</span>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <FileCheck className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="text-xs">
                  <span className="font-semibold text-foreground block">Lifecycle Validation</span>
                  <span className="text-muted-foreground">Full IQ/OQ & support</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Hero Visual & System Specs */}
          <div className="lg:col-span-5">
            <div className="relative">
              {/* Main Visual Card */}
              <Card className="border-border shadow-md overflow-hidden bg-card rounded-xl">
                <div className="relative h-64 sm:h-72 w-full bg-muted overflow-hidden">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_pat_spectrometer_inline_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_pat_spectrometer_inline_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_pat_spectrometer_inline_hero.png-wvc-sizes" alt="Bruker Process Analytical Technology Industrial Spectrometer in cleanroom manufacturing environment" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-linear-to-t from-foreground/70 via-transparent to-transparent flex items-end p-6">
                    <div className="text-background">
                      <p className="text-xs font-mono uppercase tracking-wider text-background/80">Active PAT Integration</p>
                      <p className="text-base font-semibold">Continuous Blend & Reaction Monitoring</p>
                    </div>
                  </div>
                </div>

                <CardContent className="p-5 flex flex-col gap-3">
                  <div className="flex items-center justify-between pb-2 border-b border-border text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5 font-medium text-foreground">
                      <Activity className="size-3.5 text-primary" />
                      Industrial Ruggedness
                    </span>
                    <span className="font-mono">IP65 / NEMA 4X</span>
                  </div>

                  <p className="text-xs text-muted-foreground leading-normal">
                    Equipped with RockSolid permanent alignment interferometer technology, providing unmatched vibration resistance and calibration transferability across processing lines.
                  </p>

                  <div className="pt-1 flex items-center justify-between">
                    <span className="text-xs font-semibold text-foreground">Antech Technical Application Support</span>
                    <Link to="/ApplicationConsultationPage" className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
                      Consultation
                      <ArrowRight className="size-3" />
                    </Link>
                  </div>
                </CardContent>
              </Card>

              {/* Floating feature pill */}
              <div className="hidden sm:flex absolute -bottom-4 -left-4 bg-card border border-border shadow-md rounded-lg p-3 items-center gap-3 max-w-xs">
                <div className="size-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                  <Layers className="size-4 text-primary" />
                </div>
                <div className="text-xs">
                  <p className="font-semibold text-foreground">Multi-Point Probes</p>
                  <p className="text-muted-foreground">Up to 6 measurement points</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Cards Row */}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">Instrumentation Lineup</p>
              <h2 className="text-2xl font-default font-bold text-foreground">
                Bruker Process Analytical Family
              </h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              From compact direct-mount heads to central fiber-multiplexed spectrometers, discover the right fit for your process setup.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {products.map((item, i) => <div key={item.id} data-index={i}>
                <Card className="h-full border-border bg-card hover:border-primary/40 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200 rounded-xl flex flex-col justify-between">
                  <CardContent className="p-6 flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary" className="text-xs font-medium bg-secondary text-secondary-foreground">
                        {item.badge}
                      </Badge>
                      <Sparkles className="size-4 text-muted-foreground" />
                    </div>

                    <div>
                      <h3 className="text-lg font-bold font-default text-foreground">
                        {item.name}
                      </h3>
                      <p className="text-xs font-medium text-primary mt-0.5">
                        {item.tagline}
                      </p>
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>

                  <div className="px-6 pb-6 pt-0">
                    <Button asChild variant="outline" size="sm" className="w-full justify-between group">
                      <Link to={item.link}>
                        <span>View Specifications</span>
                        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Feature Highlights Grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 bg-secondary/50 rounded-xl p-6 sm:p-8 border border-border">
          {highlights.map((highlight, i) => <div key={highlight.id} data-index={i} className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <h4 className="text-sm font-semibold text-foreground font-default">
                  {highlight.title}
                </h4>
              </div>
              <p className="text-xs text-muted-foreground pl-6 leading-relaxed">
                {highlight.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}